// A job is the unit of WORK — tasks, expenses, publications. Whether it also carries
// income is `billsOwnIncome`, decided by the server's explicit income_source, and this
// screen shows two genuinely different things depending on it:
//
//   a TOPIC inside a retainer  cost only. It has no revenue, so it has no profit —
//                              showing a zero would read as "this broke even", a claim
//                              about money that was never earned here. Its income sits
//                              on the retainer, one click away.
//   a BILLING job              the full P&L: revenue, expenses, profit, and invoices.
//                              Either standalone, or a topic that ALSO bills its own
//                              brokerage margin while sitting under a retainer.
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { jobsApi } from '../../api';
import he from '../../i18n/he';
import { issuerName } from '../../utils/issuer';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import StatTile from '../../components/StatTile';
import StatusBadge, { isJobDone } from '../../components/StatusBadge';
import Button from '../../components/Button';
import { formatMoney } from '../../components/Money';
import { CostNotes, OnRetainerNote } from '../../components/ExpenseSplit';
import AgreementsSection from '../../components/AgreementsSection';
import WorkFilesSection from '../../components/WorkFilesSection';
import { JobTermLine } from '../../components/JobTerm';
import MoneyStatus from '../../components/MoneyStatus';
import ExpensesPanel from './ExpensesPanel';
import PublicationsPanel from './PublicationsPanel';
import ListsCard from './ListsCard';
import JobModal from '../../components/JobModal';
import JobStatusModal from '../../components/JobStatusModal';
import DeleteJobModal from './DeleteJobModal';
import { isProject, engagementPath, engagementStrings } from '../../components/engagement';

// The engagement above a job, in the shape the engagement helpers read.
const parentOf = (job) => ({ id: job.retainer_id, billing_mode: job.retainer_billing_mode });

const Meta = ({ job }) => {
  const lookups = useLookups();
  return (
    <div className="card w-section w-meta">
      <div className="w-meta-row">
        <span className="muted">{he.jobs.client}: <strong>{job.client_name}</strong></span>
        {job.billsOwnIncome && job.revenue_stream && (
          <span className="muted">{he.jobs.stream}: <strong>{lookups.streamLabel(job.revenue_stream)}</strong></span>
        )}
        {/* The DEFAULT issuer — what a new charge is seeded with. Each invoice records
            who actually issued it, and they may differ; the money screen shows which. */}
        {job.billsOwnIncome && job.issuing_partner_name && (
          <span className="muted">{he.jobs.defaultIssuer}: <strong>{issuerName(job.issuing_partner_name)}</strong></span>
        )}
        <StatusBadge kind="job" value={job.status} />
        {!job.billsOwnIncome && <span className="badge gray">{he.jobs.topicBadge}</span>}
        {/* A topic that ALSO bills for itself — the melaveh malka with a photographer
            charged on. Worth calling out: it is the one job kind that appears under a
            retainer and still has a P&L of its own. */}
        {job.billsOwnIncome && job.retainer_id && (
          <span className="badge blue">{engagementStrings(parentOf(job)).ownIncome}</span>
        )}
      </div>
      {/* When the work runs. On EVERY job, topic or billing — the dates describe the
          work, and both kinds are work. It carries no money and gates nothing: a job
          whose end date passed goes on invoicing, spending and collecting exactly as
          before, which is the same line the retainer's own expired term draws. */}
      <JobTermLine term={job.term} />
      {/* The engagement above it — a retainer or a one-off project (067), named as such. */}
      {job.retainer_id && (
        <p className="muted">
          {isProject(parentOf(job)) ? he.projects.kind : he.jobs.retainer}:{' '}
          <strong>{job.retainer_title}</strong>{' · '}
          <Link to={engagementPath(parentOf(job))}>
            {isProject(parentOf(job)) ? he.projects.openProject : he.jobs.viewRetainer}
          </Link>
        </p>
      )}
      {/* What has been invoiced is the money line below — only what is left is said here. */}
      {job.agreed && (
        <p className="muted">
          {he.jobs.agreedValue}: <strong>{formatMoney(job.agreed.amount)}</strong>
          {job.agreed.remaining > 0 && (
            <>{' · '}{he.jobs.remainingToInvoice}: <strong>{formatMoney(job.agreed.remaining)}</strong></>
          )}
        </p>
      )}
      {job.shares && job.shares.length > 0 && (
        <p className="muted">
          {he.jobs.shares}: {job.shares.map((s) => `${s.partner_name} ${s.share_pct}%`).join(' · ')}
        </p>
      )}
    </div>
  );
};

// Cost alone for a job with no income of its own; the full P&L for one that bills.
//
// THE TILES ARE FIGURES ONLY — no offer to record income sits among them any more. That
// button belongs to the income card below, in its header, exactly where "הוספת הוצאה"
// sits in the expense card's. A card per kind of money, each with its own add button, is
// the whole layout of this screen; a loose button above them was the odd one out.
const Figures = ({ job }) => {
  if (!job.billsOwnIncome) {
    return (
      <div className="grid cols-3 w-section">
        {/* A job with no income of its own reports this and nothing else, so a single
            figure meaning two things costs the most here. */}
        <StatTile
          label={he.jobs.cost}
          value={formatMoney(job.cost)}
          tone="cost"
          note={<CostNotes
            paid={job.costPaid}
            unpaid={job.costUnpaid}
            toRecover={job.costToRecover}
            absorbed={job.costAbsorbed}
          />}
        />
      </div>
    );
  }
  // UNDER A RETAINER, THE SAME FIGURES ARE NAMED AS A PASS-THROUGH. Such a job exists
  // because a cost was bought and charged on to the retainer's client: to the owner its
  // "income" is that charge and its "profit" is the brokerage margin. Labels only — the
  // three values are profitCalc.forJob's, exactly as on a standalone brokerage job, which
  // keeps the plain labels.
  const passThrough = !!job.retainer_id;
  const label = {
    revenue: passThrough ? he.jobs.passThroughRevenue : he.jobs.revenue,
    expenses: passThrough ? he.jobs.passThroughCost : he.jobs.expenses,
    profit: passThrough ? he.jobs.passThroughProfit : he.jobs.profit,
  };
  return (
    <>
      <div className="grid cols-3 w-section">
        <StatTile label={label.revenue} value={formatMoney(job.profit.revenue)} />
        {/* Same figure, same tile, same treatment as the retainer's — the two screens
            must not describe the same number differently. */}
        <StatTile
          label={label.expenses}
          value={formatMoney(job.profit.expenses)}
          tone="cost"
          note={(
            <CostNotes
              paid={job.profit.expensesPaid}
              unpaid={job.profit.expensesUnpaid}
              toRecover={job.profit.expensesToRecover}
            />
          )}
        />
        <StatTile label={label.profit} value={formatMoney(job.profit.profit)} tone="green" />
      </div>
      {/* The fourth figure, not a tile: costs we absorb here net against the retainer,
          so they are in none of the three tiles, yet the expense list below shows them. */}
      {Number(job.costOnRetainer) > 0 && (
        <p className="w-under-tiles"><OnRetainerNote onRetainer={job.costOnRetainer} /></p>
      )}
    </>
  );
};

// Keyed on the id so moving to another job REMOUNTS the view below. A reload keeps
// its data on screen (dimmed) rather than blanking, which is right for a refresh of
// the same job and for a filter change elsewhere — but a different id is a different
// record, and the previous job's title and P&L must never sit under the new job's
// URL, however briefly. Remounting also resets the panels' local toggles, which
// belong to the job you were looking at.
export default function JobDetail() {
  const { id } = useParams();
  return <JobView key={id} id={id} />;
}

function JobView({ id }) {
  const navigate = useNavigate();
  const { data: job, loading, refreshing, error, reload } = useAsync(() => jobsApi.get(id), [id]);
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // After the delete this screen's record no longer exists, so it cannot reload —
  // it has to leave. A job goes back to the engagement it sat under (a retainer or a
  // one-off project), which is where the rest of the work still is; a job left standalone
  // from before 067 goes to the projects list. `replace` so Back does not return to a URL
  // that now 404s.
  const afterDelete = () => {
    const to = job.retainer_id ? engagementPath(parentOf(job)) : '/jobs';
    navigate(to, { replace: true });
  };

  return (
    <AsyncBoundary loading={loading} error={error}>
      {job && (
        <>
          <PageHeader title={job.title}>
            <Button variant="subtle" onClick={() => setEditing(true)}>{he.common.edit}</Button>
            {/* Its own button beside edit, not a field inside it: finishing work is a
                decision, while edit corrects what the job says. The same dialog opens
                from the lists this job appears on. */}
            <Button variant="subtle" onClick={() => setClosing(true)}>
              {isJobDone(job.status) ? he.jobs.reopenJob : he.jobs.closeJobAction}
            </Button>
            <Button variant="subtle" onClick={() => setDeleting(true)}>{he.common.delete}</Button>
          </PageHeader>
          {/* Dimmed while reloading: recording an invoice or a payment moves the
              figures below, and a stale profit figure should not read as the final
              one. The panels stay mounted, so the show-cancelled toggle survives. */}
          <div className={refreshing ? 'refreshing' : undefined}>
            <Meta job={job} />
            <Figures job={job} />
            {/* The job's cash status, above the costs it is earned against. It replaces
                the full income list that used to sit here: that was a second copy of the
                money screen's table, and two copies of one list is clutter whose second
                copy goes stale in the reader's head. What this screen needs from the
                money is whether anything is late or still to come in.
                Rendered on EVERY billing job, zeros included — "nothing outstanding" is
                itself an answer. A topic has no income at all, so it shows nothing. */}
            {job.money && job.billsOwnIncome && (
              <MoneyStatus money={job.money} />
            )}
            {/* The lists of the tasks on this job (078), above the costs: on an event job
                they are what the owners open every day. Not money; nothing above reads them. */}
            <ListsCard job={job} />
            <ExpensesPanel job={job} onChange={reload} />
          </div>
          {/* Outside the dimming wrapper: coverage is not money, so a reload of the
              figures above has nothing to say about whether this list is current. */}
          <PublicationsPanel job={job} />
          {/* The contracts behind the work — the photographer's, the writer's. Outside
              the dimming wrapper for the same reason coverage is: a stored document is
              not money, so a reload of the figures above has nothing to say about
              whether this list is current. The same card, from the same component, that
              holds the client's contract on the retainer screen. */}
          <AgreementsSection ownerKind="job" ownerId={job.id} />
          {/* What a content writer sent in (064). Beside the contracts because both are
              files on this job — the difference is who put them there. */}
          <WorkFilesSection ownerKind="job" ownerId={job.id} />
          {editing && (
            <JobModal job={job} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); reload(); }} />
          )}
          {/* Finishing keeps the record — the screen stays and simply reloads, which is
              the visible difference from the delete below. Every figure above reloads to
              the same number: nothing that computes them reads the status. */}
          {closing && (
            <JobStatusModal
              job={job}
              onClose={() => setClosing(false)}
              onSaved={() => { setClosing(false); reload(); }}
            />
          )}
          {deleting && (
            <DeleteJobModal job={job} onClose={() => setDeleting(false)} onDeleted={afterDelete} />
          )}
        </>
      )}
    </AsyncBoundary>
  );
}
